import { QuestionIcon, ViewIcon } from '@chakra-ui/icons'
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  AutoDisplayCondition,
  colors,
  ConsentFormTemplate,
  getAutoDisplayConditionKey,
  TemplateId,
  WithId,
} from '@hb/shared'
import { useCallback, useEffect, useState } from 'react'
import { SolidActionButton } from '../Buttons/ActionButton'
import { LoadingOverlay } from '../LoadingOverlay'
import { DefaultModal } from '../Modals/DefaultModal'
import { AutoDisplayConditionView } from './ConsentForms/AutoDisplayCondition'
import { useAssessmentResults } from './contexts/assessmentResults'

const ConsentFormConditionsPopover = ({ conditions }: { conditions: AutoDisplayCondition[] }) => (
  <Popover trigger="hover" strategy="fixed" placement="left-start">
    <PopoverTrigger>
      <IconButton
        variant="ghost"
        aria-label="View Conditions"
        color="gray.600"
        icon={<QuestionIcon w={5} h={5} />}
        size="sm"
      />
    </PopoverTrigger>
    <PopoverContent>
      <PopoverBody p={0}>
        <Flex bg="gray.100" w="100%" borderBottomWidth="1px" borderColor="blackAlpha.200" p={1}>
          <Text fontWeight={500}>Matching Conditions:</Text>
        </Flex>
        {conditions.map((condition, index) => (
          <Box px={2} py={1} key={`${getAutoDisplayConditionKey(condition)}-${index}`}>
            <AutoDisplayConditionView condition={condition} />
          </Box>
        ))}
      </PopoverBody>
      <PopoverArrow bg="gray.100" />
    </PopoverContent>
  </Popover>
)

const ConsentFormCard = ({
  form,
  conditions,
  onCheck,
  checked,
}: {
  form: WithId<ConsentFormTemplate>
  conditions: AutoDisplayCondition[]
  checked: boolean
  onCheck: (checked: boolean) => void
}) => (
  <Flex alignItems="center" w="100%" px={3} gap={2} py={1}>
    <Checkbox
      colorScheme="green"
      size="lg"
      onChange={e => onCheck(e.target.checked)}
      isChecked={checked}
    />
    <Flex justifyContent="space-between" gap={2} minW="0" flex={1}>
      <Text position="relative" top="2px" opacity={0.8} fontSize="lg" fontWeight={500}>
        {form.name}
      </Text>
      <ConsentFormConditionsPopover conditions={conditions} />
    </Flex>
  </Flex>
)

const AssessmentConfigHeader = () => (
  <Flex w="100%" alignItems="center" gap={2} py={2} px={3} borderColor="gray.200" bg="gray.100">
    <Text fontFamily="Hero-New" fontSize="xl" opacity={0.8} fontWeight={500}>
      Assessment Configuration
    </Text>
  </Flex>
)

const AssessmentConfigSubheader = ({
  title,
  bg = 'gray.50',
  icon,
}: {
  title: string
  bg?: string
  icon?: React.ReactNode
}) => (
  <Flex
    w="100%"
    alignItems="center"
    gap={2}
    py={1}
    px={3}
    borderBottomWidth="1px"
    borderTopWidth="1px"
    borderColor="gray.200"
    bg={bg}>
    {icon}
    <Text fontFamily="Hero-New" fontSize="lg" opacity={0.8} fontWeight={500}>
      {title}
    </Text>
  </Flex>
)

interface ConsentFormsConfigProps {
  checkedConsentForms: TemplateId[] | null
  onItemCheck: (consentForm: TemplateId, checked: boolean) => void
}

const ConsentFormsConfig = ({ checkedConsentForms, onItemCheck }: ConsentFormsConfigProps) => {
  const { defaultConsentForms } = useAssessmentResults()
  const { practiceId } = defaultConsentForms

  return (
    <Flex position="relative" flexFlow="column">
      <VStack spacing={0} w="100%" alignItems="flex-start">
        <AssessmentConfigSubheader title="HB Consent Forms" bg={`${colors.green.hex}30`} />
        {defaultConsentForms.hb.length ? (
          defaultConsentForms.hb.map(form => (
            <ConsentFormCard
              key={form.form.id}
              form={form.form}
              conditions={form.conditions}
              checked={
                checkedConsentForms?.some(
                  ({ practiceId: checkedPracticeId, templateId: checkedTemplateId }) =>
                    checkedPracticeId === null && checkedTemplateId === form.form.id,
                ) ?? false
              }
              onCheck={checked =>
                onItemCheck({ practiceId: null, templateId: form.form.id }, checked)
              }
            />
          ))
        ) : (
          <Text py={1} px={3}>
            No automatic HB consent forms for this pregnancy
          </Text>
        )}
        <AssessmentConfigSubheader title="Practice Consent Forms" bg={`${colors.pink.hex}40`} />
        {defaultConsentForms.practice.length ? (
          defaultConsentForms.practice.map(form => (
            <ConsentFormCard
              key={form.form.id}
              form={form.form}
              conditions={form.conditions}
              checked={
                checkedConsentForms?.some(
                  ({ practiceId: checkedPracticeId, templateId: checkedTemplateId }) =>
                    checkedPracticeId === practiceId && checkedTemplateId === form.form.id,
                ) ?? false
              }
              onCheck={checked => onItemCheck({ practiceId, templateId: form.form.id }, checked)}
            />
          ))
        ) : (
          <Text py={1} px={3}>
            No automatic practice consent forms for this pregnancy
          </Text>
        )}
      </VStack>
    </Flex>
  )
}

interface ViewableBeforeCallConfigProps {
  viewableBeforeCall: boolean
  onViewableBeforeCallChange: (checked: boolean) => void
}

const ViewableBeforeCallConfig = ({
  viewableBeforeCall,
  onViewableBeforeCallChange,
}: ViewableBeforeCallConfigProps) => (
  <Flex position="relative" flexFlow="column">
    <AssessmentConfigSubheader icon={<ViewIcon />} title="Visibility" />
    <VStack spacing={0} w="100%" alignItems="flex-start" divider={<Divider />}>
      <Flex alignItems="center" w="100%" px={3} gap={2} py={2}>
        <Checkbox
          colorScheme="green"
          size="lg"
          onChange={e => onViewableBeforeCallChange(e.target.checked)}
          isChecked={viewableBeforeCall}
        />
        <Text position="relative" top="2px" opacity={0.8} fontSize="lg" fontWeight={500}>
          Assessment viewable before HB call
        </Text>
      </Flex>
    </VStack>
  </Flex>
)

const AssessmentConfig = () => {
  const {
    defaultConsentForms,
    loadingDefaultConsentForms,
    onConfirmAssessmentConfig,
    confirmingConsentFormsForResults,
  } = useAssessmentResults()

  const [checkedConsentForms, setCheckedConsentForms] = useState<TemplateId[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [viewableBeforeCall, setViewableBeforeCall] = useState(true)
  const { practiceId } = defaultConsentForms

  useEffect(() => {
    if (confirmingConsentFormsForResults) {
      setViewableBeforeCall(true)
    }
  }, [confirmingConsentFormsForResults])

  useEffect(() => {
    if (checkedConsentForms || loadingDefaultConsentForms || !practiceId) return
    if (defaultConsentForms) {
      setCheckedConsentForms([
        ...defaultConsentForms.hb.map(({ form }) => ({
          practiceId: null,
          templateId: form.id,
        })),

        ...defaultConsentForms.practice.map(({ form }) => ({
          practiceId,
          templateId: form.id,
        })),
      ])
    } else {
      setCheckedConsentForms(null)
    }
  }, [defaultConsentForms, checkedConsentForms, loadingDefaultConsentForms, practiceId])

  const handleItemCheck = useCallback(
    (consentForm: TemplateId, checked: boolean) => {
      if (!checkedConsentForms) return
      if (checked) {
        setCheckedConsentForms([...checkedConsentForms, consentForm])
      } else {
        setCheckedConsentForms(
          checkedConsentForms.filter(
            ({ practiceId: checkedPracticeId, templateId: checkedTemplateId }) =>
              checkedPracticeId !== consentForm.practiceId ||
              checkedTemplateId !== consentForm.templateId,
          ),
        )
      }
    },
    [checkedConsentForms],
  )

  const handleViewableBeforeCallChange = useCallback((checked: boolean) => {
    setViewableBeforeCall(checked)
  }, [])

  const handleConfirm = useCallback(() => {
    if (!checkedConsentForms) return
    setLoading(true)
    onConfirmAssessmentConfig(checkedConsentForms, viewableBeforeCall).finally(() =>
      setLoading(false),
    )
  }, [checkedConsentForms, viewableBeforeCall, onConfirmAssessmentConfig])

  return (
    <Flex position="relative" flexFlow="column">
      <AssessmentConfigHeader />
      <ViewableBeforeCallConfig
        viewableBeforeCall={viewableBeforeCall}
        onViewableBeforeCallChange={handleViewableBeforeCallChange}
      />
      <ConsentFormsConfig checkedConsentForms={checkedConsentForms} onItemCheck={handleItemCheck} />
      {/* Additional configuration sections can be added here */}
      <Flex
        w="100%"
        borderTopWidth="1px"
        borderColor="gray.200"
        bg="gray.100"
        justifyContent="flex-end"
        gap={2}
        py={2}
        px={3}>
        <SolidActionButton size="sm" onClick={handleConfirm} isLoading={loading}>
          Confirm Assessment Config
        </SolidActionButton>
      </Flex>
      <LoadingOverlay text="Sending assessment..." loading={loading} />
    </Flex>
  )
}

export const AssessmentConfigModal = () => {
  const { confirmingConsentFormsForResults, onCancelConsentFormsForResults } =
    useAssessmentResults()

  return (
    <DefaultModal
      overlayHeader
      isOpen={!!confirmingConsentFormsForResults}
      onClose={onCancelConsentFormsForResults}
      render={() => <AssessmentConfig />}
    />
  )
}
